import { useEffect, useMemo } from 'react'

import { isNativeCurrency } from '../../config/native'
import { Currency, CurrencyAmount, JSBI, Token, TokenAmount } from '../../sdk'
import { ERC20_ABI } from '../../constants/abis/erc20'
import type { Abi } from 'viem'

const erc20Abi = ERC20_ABI as Abi
import { useAllTokens } from '../../hooks/Tokens'
import { useAppWeb3 } from '../../hooks'
import { useMulticallContract } from '../../hooks/useContract'
import { isAddress } from '../../utils'
import { isReadBalancesUiDebugEnabled, readBalancesUiDebug } from '../../utils/debugReadBalancesUi'
import { useSingleContractMultipleData, useMultipleContractSingleData } from '../multicall/hooks'

/**
 * Returns a map of the given addresses to their eventually consistent ETH balances.
 */
export function useETHBalances(uncheckedAddresses?: (string | undefined)[]): {
  [address: string]: CurrencyAmount | undefined
} {
  const { chainId } = useAppWeb3()
  const multicallContract = useMulticallContract()

  const addresses: string[] = useMemo(
    () =>
      uncheckedAddresses
        ? uncheckedAddresses
            .map(isAddress)
            .filter((a): a is string => a !== false)
            .sort()
        : [],
    [uncheckedAddresses],
  )

  const results = useSingleContractMultipleData(
    multicallContract,
    'getEthBalance',
    addresses.map((address) => [address]),
  )

  const ethMap = useMemo(
    () =>
      addresses.reduce<{ [address: string]: CurrencyAmount }>((memo, address, i) => {
        const value = results?.[i]?.result?.[0]
        // `if (value)` omitía saldo 0 si el decode devolviera bigint 0n (falsy).
        if (value !== undefined && value !== null && chainId != null) {
          memo[address] = CurrencyAmount.native(chainId, JSBI.BigInt(value.toString()))
        }
        return memo
      }, {}),
    [addresses, results, chainId],
  )

  useEffect(() => {
    if (!isReadBalancesUiDebugEnabled()) return
    readBalancesUiDebug('useETHBalances', {
      hasMulticallContract: Boolean(multicallContract),
      addresses,
      callStates: addresses.map((addr, i) => {
        const r = results[i]
        return {
          address: addr,
          loading: r?.loading,
          syncing: r?.syncing,
          error: r?.error,
          valid: r?.valid,
          wei: r?.result?.[0] != null ? String(r.result[0]) : undefined,
        }
      }),
      aggregatedKeys: Object.keys(ethMap),
    })
  }, [multicallContract, addresses, results, ethMap])

  return ethMap
}

/**
 * Returns a map of token addresses to their eventually consistent token balances for a single account.
 */
export function useTokenBalancesWithLoadingIndicator(
  address?: string,
  tokens?: (Token | undefined)[],
): [{ [tokenAddress: string]: TokenAmount | undefined }, boolean] {
  const validatedTokens: Token[] = useMemo(
    () => tokens?.filter((t?: Token): t is Token => isAddress(t?.address) !== false) ?? [],
    [tokens],
  )

  const validatedTokenAddresses = useMemo(() => validatedTokens.map((vt) => vt.address), [validatedTokens])

  const balances = useMultipleContractSingleData(validatedTokenAddresses, erc20Abi, 'balanceOf', [address])

  const anyLoading: boolean = useMemo(() => balances.some((callState) => callState.loading), [balances])

  const tokenMap = useMemo(
    () =>
      address && validatedTokens.length > 0
        ? validatedTokens.reduce<{ [tokenAddress: string]: TokenAmount | undefined }>((memo, token, i) => {
            const value = balances?.[i]?.result?.[0]
            if (value !== undefined && value !== null) {
              const amount = JSBI.BigInt(value.toString())
              memo[token.address] = new TokenAmount(token, amount)
            }
            return memo
          }, {})
        : {},
    [address, validatedTokens, balances],
  )

  useEffect(() => {
    if (!isReadBalancesUiDebugEnabled()) return
    readBalancesUiDebug('useTokenBalances', {
      account: address,
      tokenAddresses: validatedTokenAddresses,
      rows: validatedTokens.map((t, i) => {
        const r = balances[i]
        const value = r?.result?.[0]
        return {
          symbol: t.symbol,
          address: t.address,
          loading: r?.loading,
          syncing: r?.syncing,
          error: r?.error,
          rawBalance: value != null ? String(value) : undefined,
          mappedExact: tokenMap[t.address]?.toExact?.(),
        }
      }),
      anyLoading,
    })
  }, [address, validatedTokenAddresses, validatedTokens, balances, tokenMap, anyLoading])

  return [tokenMap, anyLoading]
}

export function useTokenBalances(
  address?: string,
  tokens?: (Token | undefined)[],
): { [tokenAddress: string]: TokenAmount | undefined } {
  return useTokenBalancesWithLoadingIndicator(address, tokens)[0]
}

// get the balance for a single token/account combo
export function useTokenBalance(account?: string, token?: Token): TokenAmount | undefined {
  const tokenBalances = useTokenBalances(account, [token])
  if (!token) return undefined
  return tokenBalances[token.address]
}

export function useCurrencyBalances(
  account?: string,
  currencies?: (Currency | undefined)[],
): (CurrencyAmount | undefined)[] {
  /** Debe coincidir con las claves de `useETHBalances` (checksummed); wagmi a veces expone la cuenta en minúsculas. */
  const accountChecksum = useMemo(() => {
    if (!account) return undefined
    const a = isAddress(account)
    return a !== false ? a : undefined
  }, [account])

  const tokens = useMemo(
    () => currencies?.filter((currency): currency is Token => currency instanceof Token) ?? [],
    [currencies],
  )

  const { chainId } = useAppWeb3()
  const tokenBalances = useTokenBalances(accountChecksum, tokens)
  const containsNative: boolean = useMemo(
    () => (chainId != null ? currencies?.some((currency) => currency && isNativeCurrency(currency, chainId)) : false) ?? false,
    [currencies, chainId],
  )
  const ethBalance = useETHBalances(containsNative && accountChecksum ? [accountChecksum] : [])

  const amounts = useMemo(
    () =>
      currencies?.map((currency) => {
        if (!accountChecksum || !currency) return undefined
        if (currency instanceof Token) return tokenBalances[currency.address]
        if (chainId != null && isNativeCurrency(currency, chainId)) return ethBalance[accountChecksum]
        return undefined
      }) ?? [],
    [accountChecksum, chainId, currencies, ethBalance, tokenBalances],
  )

  useEffect(() => {
    if (!isReadBalancesUiDebugEnabled()) return
    readBalancesUiDebug('useCurrencyBalances', {
      accountRaw: account,
      accountChecksum,
      containsNative,
      ethMapKeys: Object.keys(ethBalance),
      ethKeyPresent: accountChecksum ? Boolean(ethBalance[accountChecksum]) : false,
      perCurrency: currencies?.map((c, i) => ({
        symbol: c?.symbol,
        isNative: chainId != null && c != null && isNativeCurrency(c, chainId),
        tokenAddress: c instanceof Token ? c.address : undefined,
        amountExact: amounts[i]?.toExact?.(),
        reasonUndefined:
          !accountChecksum || !c
            ? 'no-account-or-currency'
            : chainId != null && c != null && isNativeCurrency(c, chainId)
              ? !ethBalance[accountChecksum]
                ? 'no-native-in-map'
                : 'ok-native'
              : c instanceof Token && !tokenBalances[c.address]
                ? 'no-token-in-map'
                : 'ok-token',
      })),
    })
  }, [account, accountChecksum, chainId, containsNative, currencies, ethBalance, tokenBalances, amounts])

  return amounts
}

export function useCurrencyBalance(account?: string, currency?: Currency): CurrencyAmount | undefined {
  return useCurrencyBalances(account, [currency])[0]
}

// mimics useAllBalances
export function useAllTokenBalances(): { [tokenAddress: string]: TokenAmount | undefined } {
  const { account } = useAppWeb3()
  const allTokens = useAllTokens()
  const allTokensArray = useMemo(() => Object.values(allTokens ?? {}), [allTokens])
  const balances = useTokenBalances(account ?? undefined, allTokensArray)
  return balances ?? {}
}
